// frontend/src/components/admin/tenants/TenantsOuTreeView.tsx
/**
 * View principal da pagina /admin/tenants — substitui TenantsGroupedView.
 *
 * Renderiza a arvore mesclada (UO pai > Sub-UO > Conta) construida pelo
 * buildTenantOuTree. Recebe tenants ja filtrados pelo caller (Tenants.tsx).
 */
import { useState, useMemo, useCallback } from 'react'
import {
  Stack,
  Skeleton,
  Card,
  Text,
  Button,
  ThemeIcon,
  Alert,
} from '@mantine/core'
import {
  IconBuildingFactory2,
  IconPlus,
  IconAlertTriangle,
} from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import type { TenantRow } from './TenantsTable'
import type { AwsOuNode } from '@/api/awsOrgProvisioning'
import { buildTenantOuTree } from './ouTree/tenantOuTreeBuilder'
import { OuBranchNode, OU_EXPANDED_STORAGE_KEY } from './ouTree/OuBranchNode'
import { OrphansBranchNode } from './ouTree/OrphansBranchNode'
import type { MergedTreeNode } from './ouTree/types'

interface Props {
  /** Tenants ja filtrados pelo Tenants.tsx (search + filters). */
  tenants: TenantRow[]
  /** Tenants cru, sem filtro — usado pra montar tenantIdByOuId completo. */
  allTenants?: TenantRow[]
  ouNodes: AwsOuNode[]
  ouTreeError: boolean
  loading: boolean
  searchQuery: string
  hasActiveFilter: boolean
  onToggleField: (id: number, field: string, value: boolean) => void
  onToggleZabbix: (tenantId: number, enabled: boolean) => void
  onToggleGitlab: (tenantId: number, currentlyEnabled: boolean) => void
  onToggleWhatsApi: (tenantId: number, enabled: boolean) => void
  onUpdateTimezone?: (tenantId: number, timezone: string | null) => void
  onEditRegions?: (tenant: TenantRow) => void
  onProvisioningFailedClick?: (tenant: TenantRow) => void
  fieldPendingIds?: Set<number>
  whatsapiPendingIds?: Set<number>
  timezonePendingIds?: Set<number>
}

function loadExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(OU_EXPANDED_STORAGE_KEY)
    if (!raw) return new Set()
    return new Set(JSON.parse(raw) as string[])
  } catch {
    return new Set()
  }
}

function saveExpanded(set: Set<string>) {
  try {
    localStorage.setItem(OU_EXPANDED_STORAGE_KEY, JSON.stringify([...set]))
  } catch {
    /* ignore */
  }
}

export function TenantsOuTreeView({
  tenants,
  allTenants,
  ouNodes,
  ouTreeError,
  loading,
  searchQuery,
  hasActiveFilter,
  ...rowProps
}: Props) {
  const navigate = useNavigate()
  const [expandedSet, setExpandedSet] = useState<Set<string>>(loadExpanded)

  const onToggleExpand = useCallback((ouId: string) => {
    setExpandedSet((prev) => {
      const next = new Set(prev)
      if (next.has(ouId)) next.delete(ouId)
      else next.add(ouId)
      saveExpanded(next)
      return next
    })
  }, [])

  const tenantIdByOuId = useMemo(() => {
    const map = new Map<string, number>()
    const src = allTenants ?? tenants
    for (const t of src) {
      if (t.ou_id && !t.ou_id.startsWith('auto:')) {
        map.set(String(t.ou_id), t.id)
      }
    }
    return map
  }, [allTenants, tenants])

  const effectiveOuNodes = ouTreeError ? [] : ouNodes

  const tree = useMemo<MergedTreeNode[]>(() => {
    return buildTenantOuTree(tenants, effectiveOuNodes, {
      pruneEmptyParents: searchQuery.trim().length > 0 || hasActiveFilter,
      searchQuery,
    })
  }, [tenants, effectiveOuNodes, searchQuery, hasActiveFilter])

  if (loading) return <ListSkeleton />

  const hasOus = ouNodes.length > 0
  const hasTenants = tenants.length > 0
  const empty = !hasOus && !hasTenants

  if (empty) {
    return (
      <Card withBorder radius="md" p="xl">
        <Stack align="center" gap="md" py="xl">
          <ThemeIcon size={56} radius="xl" variant="light" color="blue">
            <IconBuildingFactory2 size={28} />
          </ThemeIcon>
          <Stack gap={4} align="center">
            <Text fw={600} size="lg">Nenhum tenant ainda</Text>
            <Text size="sm" c="dimmed" ta="center" maw={420}>
              Crie seu primeiro tenant AWS para começar a gerenciar contas e
              ambientes a partir do ConsoleAI.
            </Text>
          </Stack>
          <Button leftSection={<IconPlus size={14} />} onClick={() => navigate('/admin/tenants/new')}>
            Novo tenant
          </Button>
        </Stack>
      </Card>
    )
  }

  return (
    <Stack gap="md" data-testid="tenants-ou-tree-view">
      {ouTreeError && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
          Hierarquia AWS indisponível. Mostrando lista flat dos tenants.
        </Alert>
      )}
      {hasOus && !hasTenants && (
        <Alert color="blue" icon={<IconAlertTriangle size={16} />}>
          Nenhuma conta provisionada ainda. A estrutura de OUs do AWS Organizations
          esta visível abaixo.
        </Alert>
      )}
      <Stack gap="md">
        {tree.map((node) => {
          if (node.type === 'orphan_root') {
            return <OrphansBranchNode key="orphans" branch={node} rowProps={rowProps} />
          }
          if (node.type === 'ou') {
            return (
              <OuBranchNode
                key={`ou-${node.node.ou_id}`}
                branch={node}
                depth={0}
                expandedSet={expandedSet}
                onToggleExpand={onToggleExpand}
                tenantIdByOuId={tenantIdByOuId}
                rowProps={rowProps}
              />
            )
          }
          return null
        })}
      </Stack>
    </Stack>
  )
}

function ListSkeleton() {
  return (
    <Stack gap="md">
      {[0, 1, 2, 3].map((i) => (
        <Card key={i} withBorder radius="md" p="md">
          <Skeleton height={14} width={140} />
          <Skeleton height={10} width={200} mt="xs" />
        </Card>
      ))}
    </Stack>
  )
}
